import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import { sortVideos, type SortKey } from "@/utils/video";
import { usePageMeta } from "@/hooks/usePageMeta";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新上传" },
  { key: "hot", label: "最热观看" },
  { key: "oldest", label: "最早发布" },
];

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const category = categories.find((c) => c.slug === categorySlug);

  usePageMeta(
    category ? `${category.name} - 黄色免费视频` : "分类未找到 - 黄色免费视频",
    category
      ? `黄色免费视频${category.name}分类，${category.description}`
      : "该分类暂不存在。",
    category ? `${category.name},免费视频,${category.name}视频,在线观看` : "免费视频分类",
  );

  const [sort, setSort] = useState<SortKey>("latest");
  const [activeTag, setActiveTag] = useState<string>("全部");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  const categoryVideos = useMemo(
    () => videos.filter((v) => v.category === categorySlug),
    [categorySlug],
  );

  const tags = useMemo(() => {
    const set = new Set<string>();
    categoryVideos.forEach((v) => v.tags.forEach((t) => set.add(t)));
    return ["全部", ...Array.from(set)];
  }, [categoryVideos]);

  const filtered = useMemo(() => {
    const base =
      activeTag === "全部"
        ? categoryVideos
        : categoryVideos.filter((v) => v.tags.includes(activeTag));
    return sortVideos(base, sort);
  }, [categoryVideos, activeTag, sort]);

  useEffect(() => {
    setSort("latest");
    setActiveTag("全部");
    setVisible(PAGE_SIZE);
    setFilterOpen(false);
    window.scrollTo({ top: 0 });
  }, [categorySlug]);

  const resetFilters = () => {
    setSort("latest");
    setActiveTag("全部");
    setVisible(PAGE_SIZE);
  };

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 w-full px-4 md:px-6 py-20 flex flex-col items-center justify-center text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
            <i className="ri-error-warning-line text-3xl" />
          </span>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">
            未找到该分类
          </h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的分类不存在或已下线。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-6 pt-6 md:pt-8">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line" />
            </span>
            <span className="text-foreground-800 font-medium">{category.name}</span>
          </nav>

          <div className="mt-4 flex items-start gap-4">
            <span className="shrink-0 w-14 h-14 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <i className={`${category.icon} text-3xl`} />
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
                {category.name} - 黄色免费视频
              </h1>
              <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl">
                {category.description}
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 mt-5">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors whitespace-nowrap ${
                  c.slug === category.slug
                    ? "bg-primary-500 border-primary-500 text-background-50"
                    : "bg-background-50 border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={`${c.icon} text-base`} />
                {c.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 mt-6">
          <div className="rounded-lg bg-background-100 border border-background-200">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 lg:cursor-default cursor-pointer"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-filter-3-line" />
                </span>
                筛选与排序
                <span className="text-xs font-normal text-foreground-500">
                  共 {filtered.length} 个视频
                </span>
              </span>
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500 lg:hidden">
                <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
              </span>
            </button>

            <div className={`${filterOpen ? "block" : "hidden"} lg:block px-4 pb-4 pt-1 border-t border-background-200`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-3">
                <div>
                  <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2">
                    排序方式
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {sortOptions.map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => {
                          setSort(o.key);
                          setVisible(PAGE_SIZE);
                        }}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                          sort === o.key
                            ? "bg-secondary-900 border-secondary-900 text-background-50"
                            : "bg-background-50 border-background-300 text-foreground-700 hover:border-secondary-400"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2">
                    标签筛选
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          setActiveTag(t);
                          setVisible(PAGE_SIZE);
                        }}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                          activeTag === t
                            ? "bg-accent-500 border-accent-500 text-background-50"
                            : "bg-background-50 border-background-300 text-foreground-700 hover:border-accent-400"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8">
          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {shown.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisible((n) => n + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 h-11 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-add-line" />
                    </span>
                    加载更多
                  </button>
                ) : (
                  <p className="text-sm text-foreground-500">已经到底啦，去看看其他分类吧</p>
                )}
                <p className="text-xs text-foreground-400">
                  已显示 {shown.length} / {filtered.length} 个视频
                </p>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-16">
              <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-950">
                暂无匹配的视频
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                试试更换标签或重置筛选条件。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-refresh-line" />
                </span>
                重置筛选
              </button>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}